import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';

const phases = [
  {
    icon: 'ri-user-voice-line',
    image:
      'https://readdy.ai/api/search-image?query=One-on-one%20professional%20consultation%20meeting%20between%20a%20business%20consultant%20and%20a%20food%20truck%20entrepreneur%20at%20a%20modern%20bright%20office%20desk%20with%20laptop%20and%20regulatory%20documents%20spread%20out%2C%20bright%20natural%20office%20lighting%20illuminating%20faces%2C%20collaborative%20focused%20atmosphere%2C%20clean%20contemporary%20interior%20design%20with%20subtle%20teal%20accent%20decor&width=1400&height=900&seq=agency1c&orientation=landscape',
  },
  {
    icon: 'ri-file-list-3-line',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20hands%20organizing%20and%20filing%20permit%20paperwork%20and%20regulatory%20documents%20on%20a%20clean%20modern%20desk%20with%20official%20stamps%20folders%20and%20binders%2C%20bright%20natural%20office%20lighting%2C%20meticulously%20stacked%20paperwork%20with%20city%20seal%20stamps%20visible%2C%20organized%20administrative%20workspace%20with%20subtle%20cool%20tones&width=1400&height=900&seq=agency2c&orientation=landscape',
  },
  {
    icon: 'ri-search-line',
    image:
      'https://readdy.ai/api/search-image?query=Close-up%20of%20regulatory%20compliance%20documents%20and%20health%20department%20guidelines%20being%20reviewed%20with%20a%20magnifying%20glass%20and%20teal%20highlighter%20marker%20on%20a%20modern%20desk%2C%20organized%20analytical%20workspace%20with%20charts%20and%20checklists%2C%20professional%20office%20setting%20with%20bright%20ambient%20lighting%20and%20clean%20aesthetic&width=1400&height=900&seq=agency3c&orientation=landscape',
  },
  {
    icon: 'ri-shield-check-line',
    image:
      'https://readdy.ai/api/search-image?query=Triumphant%20moment%20showing%20an%20approved%20official%20food%20truck%20permit%20certificate%20being%20held%20up%20proudly%20with%20a%20blurred%20colorful%20food%20truck%20visible%20in%20the%20background%2C%20bright%20midday%20sunlight%20creating%20a%20glow%20of%20success%20and%20achievement%2C%20professional%20celebratory%20atmosphere%20with%20confetti-like%20paper%20particles%20in%20air&width=1400&height=900&seq=agency4c&orientation=landscape',
  },
];

export default function HeroBanner() {
  const { t } = useTranslation();
  const phaseLabels = t('hero.phases', { returnObjects: true }) as string[];
  const [stage, setStage] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAutoPlay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setStage((s) => (s + 1) % phases.length);
    }, 4000);
  }, []);

  useEffect(() => {
    startAutoPlay();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startAutoPlay]);

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative w-full h-screen min-h-[700px] flex items-center justify-center overflow-hidden"
    >
      {/* Background images with crossfade */}
      <div className="absolute inset-0 w-full h-full">
        <AnimatePresence mode="sync">
          <motion.div
            key={stage}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.04 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
            className="absolute inset-0 w-full h-full"
          >
            <img
              src={phases[stage].image}
              alt={phaseLabels[stage]}
              className="w-full h-full object-cover object-top"
            />
          </motion.div>
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60" />
      </div>

      {/* Scanline */}
      <div className="scanline" />

      {/* Content */}
      <div className="relative z-10 w-full text-center px-4 flex flex-col items-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="inline-flex items-center gap-2 bg-teal-400/20 border border-teal-400/40 backdrop-blur-sm text-teal-300 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-8"
        >
          <i className="ri-map-pin-2-fill text-teal-400" />
          {t('hero.badge')}
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: 'easeOut' }}
          className="text-white text-4xl md:text-6xl lg:text-7xl font-bold leading-tight max-w-5xl"
        >
          {t('hero.headline1')}
          <br />
          <span className="text-teal-300 italic">{t('hero.headline2')}</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
          className="mt-6 text-white/80 text-base md:text-xl max-w-2xl leading-relaxed"
        >
          {t('hero.subtitle')}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
          className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
        >
          <button
            onClick={() => scrollTo('#consultation')}
            className="bg-teal-500 hover:bg-teal-600 text-white font-semibold text-sm px-8 py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center"
          >
            {t('hero.bookSession')}
            <i className="ri-calendar-line" />
          </button>
          <button
            onClick={() => scrollTo('#services')}
            className="bg-white/10 border border-white/40 hover:bg-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full backdrop-blur-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center"
          >
            {t('hero.exploreServices')}
            <i className="ri-arrow-down-line" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}